import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/utils/cn";

type ButtonVariant = "default" | "primary" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "success";
type ButtonSize = "default" | "sm" | "lg" | "icon";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    fullWidth?: boolean;
    loading?: boolean;
    testId?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "default", size = "default", fullWidth = false, loading = false, disabled, children, testId, ...props }, ref) => {
        const baseStyles = cn(
            "inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium transition-all duration-200 cursor-pointer",
            "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:pointer-events-none disabled:opacity-50",
            "active:scale-[0.98]", // Tactile click feedback
            "select-none" // Prevent text selection
        );

        const variants: Record<ButtonVariant, string> = {
            // Gradient brand button
            default: cn(
                "bg-linear-to-r from-[#3b82f6] to-[#011557] text-white hover:text-blue-600",
                "hover:from-[#D8E1FF] hover:to-[#D8E1FF]  hover:border-blue-500", // Light hover with border
                "border border-transparent",
                "shadow-md shadow-blue-500/10"
            ),
            // Solid primary color
            primary: cn(
                "bg-blue-600 text-white",
                "hover:bg-blue-700 hover:shadow-md",
                "shadow-sm"
            ),
            // Destructive action
            destructive: cn(
                "bg-[#e85347] text-white",
                "hover:bg-red-600 hover:shadow-red-500/20 hover:border-red-500",
                "shadow-sm"
            ),
            // Success action (Green)
            success: cn(
                "bg-emerald-600 text-white",
                "hover:bg-emerald-700 hover:shadow-emerald-500/20",
                "shadow-sm"
            ),
            // Outline / Bordered
            outline: cn(
                "border border-gray-200 dark:border-gray-700",
                "bg-white dark:bg-gray-800",
                "text-gray-900 dark:text-gray-100",
                "hover:bg-gray-50 dark:hover:bg-gray-750",
                "hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-800",
                "shadow-sm"
            ),
            // Secondary Gray
            secondary: cn(
                "bg-gray-100 dark:bg-gray-800",
                "text-gray-900 dark:text-white",
                "hover:bg-gray-200 dark:hover:bg-gray-700",
                "border border-transparent"
            ),
            // Ghost (Transparent)
            ghost: cn(
                "hover:bg-gray-100 dark:hover:bg-gray-800",
                "text-gray-700 dark:text-gray-300",
                "hover:text-gray-900 dark:hover:text-white"
            ),
            // Link style
            link: "text-blue-600 underline-offset-4 hover:underline dark:text-blue-500 p-0 h-auto font-normal active:scale-100"
        };

        const sizes: Record<ButtonSize, string> = {
            default: "h-10 px-4 py-2",
            sm: "h-9 rounded-sm px-3 text-xs",
            lg: "h-12 rounded-sm px-8 text-base",
            icon: "h-10 w-10 p-2",
        };

        return (
            <button
                ref={ref}
                disabled={disabled || loading}
                className={cn(
                    baseStyles,
                    variants[variant],
                    sizes[size],
                    fullWidth && "w-full",
                    className
                )}
                data-testid={testId}
                id={props.id}
                {...props}
            >
                {loading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin text-current" />
                )}
                {children}
            </button>
        );
    }
);

Button.displayName = "Button";

export { Button };
export default Button;

